import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, } from 'react';
import { Box, Typography, Skeleton, Button, CircularProgress, } from '@mui/material';
import { useConversationStore } from '../../ConversationStoreProvider';
import { replaceStylesheetGlobals } from '../../portal/theme/portalTheme';
import { useTokens } from '../../tokens/TokensProvider';
import { useIsMobileViewContext } from '../IsMobileViewContext';
import { mapProductCardConfigToFormatting } from './productCardConfig';
import { resolveAiSearchConfig } from './aiSearchConfig';
import VariantSelectorAdvanced from './VariantSelector';
import DescriptionRenderer from './DescriptionRenderer';
import CheckoutFlow from './CheckoutFlow';
import QuantityField from './QuantityField';
import getProductData from './getProductData';
import { validateVariantSelection, getSelectedVariant, shouldAutoSelectVariant, syncVariantStates, getButtonState, } from './variantValidationUtils';
import formatPriceWithLocale from '../utils/formatPriceWithLocale';
const ProductDetailPanel = ({ product, }) => {
    const store = useConversationStore();
    const tokens = useTokens();
    const isMobileView = useIsMobileViewContext();
    // Get stylesheet from conversation store for color resolution - UNIFIED with EnhancedProductCard
    const { stylesheet: storeStylesheet } = store;
    // Get locale from customerContext, fallback to 'en'
    const locale = useMemo(() => store?.customerContext?.currentPage?.locale
        || store?.customerContext?.locale
        || 'en', [store?.customerContext]);
    const currency = useMemo(() => store?.customerContext?.currentPage?.currency
        || store?.customerContext?.currency
        || 'USD', [store?.customerContext]);
    // State management
    const [baseImageId, setBaseImageId] = useState(null);
    const [hoveredImageId, setHoveredImageId] = useState(null);
    const [selectedVariantId, setSelectedVariantId] = useState(null);
    const [selectedVariantOptions, setSelectedVariantOptions] = useState({});
    const [productData, setProductData] = useState(null);
    const [isLoadingProduct, setIsLoadingProduct] = useState(true);
    // Flag to prevent conflicts between variant hover and gallery hover
    const variantHoverActiveRef = React.useRef(false);
    // Flag to prevent automatic variant-image sync when user manually selects gallery images
    const userSelectedImageRef = React.useRef(false);
    // Add to cart states
    const [quantity, setQuantity] = useState(1);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [addToCartError, setAddToCartError] = useState(null);
    const [isAddedToCart, setIsAddedToCart] = useState(false);
    const [addToCartTimeoutId, setAddToCartTimeoutId] = useState(null);
    // Unique instance ID for this specific ProductDetailPanel instance
    const instanceId = React.useRef(`product-detail-${Math.random().toString(36).substr(2, 9)}`).current;
    // Ref for scroll
    const sidebarRef = React.useRef(null);
    const checkoutFlowRef = React.useRef(null);
    // Reset all state when product changes
    React.useEffect(() => {
        // Reset all state to pristine values
        setBaseImageId(null);
        setHoveredImageId(null);
        setSelectedVariantId(null);
        setSelectedVariantOptions({});
        setProductData(null);
        setIsLoadingProduct(true);
        setQuantity(1);
        setIsAddingToCart(false);
        setAddToCartError(null);
        setIsAddedToCart(false);
        // Clear any pending timeouts
        if (addToCartTimeoutId) {
            clearTimeout(addToCartTimeoutId);
            setAddToCartTimeoutId(null);
        }
        // Reset refs
        variantHoverActiveRef.current = false;
        userSelectedImageRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.id, product?.title, product?.handle]);
    // Reset cart state when variant or quantity changes
    React.useEffect(() => {
        if (isAddedToCart) {
            setIsAddedToCart(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVariantId, selectedVariantOptions, quantity]);
    // Image display logic - priority: hoveredImageId || baseImageId
    const displayImageId = hoveredImageId || baseImageId;
    // Helper functions for image-variant synchronization
    function findImageForVariant(variantId, images, variantList) {
        const exactMatch = images.find((img) => img.variantId === variantId);
        if (exactMatch)
            return exactMatch.id;
        const variant = variantList.find((v) => v.id === variantId);
        if (variant?.featured_media?.id) {
            const mediaMatch = images.find((img) => img.mediaId === variant.featured_media.id);
            if (mediaMatch)
                return mediaMatch.id;
        }
        return null;
    }
    function findImageForVariantCombination(selection, images) {
        const exactMatch = images.find((img) => {
            if (!img.variantCombination)
                return false;
            return Object.entries(selection).every(([, value]) => {
                const key = Object.keys(selection)[Object.values(selection).indexOf(value)];
                return img.variantCombination[key] === value;
            });
        });
        if (exactMatch)
            return exactMatch.id;
        return null;
    }
    function getVariantForThumbnail(imageId, images) {
        const image = images.find((img) => img.id === imageId);
        if (!image)
            return { variantId: null, variantCombination: null };
        return {
            variantId: image.variantId || null,
            variantCombination: image.variantCombination || null,
        };
    }
    const syncImageWithVariantSelection = React.useCallback((selection, images, variantList) => {
        const combinationImage = findImageForVariantCombination(selection, images);
        if (combinationImage)
            return combinationImage;
        const selectedVariant = getSelectedVariant(null, // No selectedVariantId in this context
        selection, variantList, productData?.options || []);
        if (selectedVariant) {
            const variantImage = findImageForVariant(selectedVariant.id, images, variantList);
            if (variantImage)
                return variantImage;
        }
        const fallbackImage = images[0]?.id || null;
        return fallbackImage;
    }, [productData?.options]);
    // Component configuration
    const resolvedClientAIConfig = store?.clientAIConfig;
    const resolvedProductFormatting = useMemo(() => {
        const rawConfig = resolvedClientAIConfig?.productCardConfig;
        const adminFormatting = rawConfig
            ? mapProductCardConfigToFormatting(rawConfig)
            : undefined;
        const base = {
            elementOrder: ['title', 'price', 'description', 'variants', 'addToCartButton', 'reviews', 'productTabs'],
            showTitle: true,
            showPrice: true,
            showDescription: true,
            showVariants: true,
            showReviews: false,
            showProductTabs: false,
            showAddToCartButton: true,
        };
        const merged = {
            ...base,
            ...(adminFormatting || {}),
        };
        return merged;
    }, [resolvedClientAIConfig?.productCardConfig]);
    const allowedElements = useMemo(() => {
        // ProductDetailPanel has its own element order
        const base = ['title', 'price', 'description', 'variants', 'addToCartButton']; // TODO: add 'reviews'
        const filtered = base.filter((elementType) => {
            let shouldShow;
            switch (elementType) {
                case 'addToCartButton':
                    // Level 1: Global add to cart functionality must be enabled
                    if (!resolvedClientAIConfig?.addToCartEnabled) {
                        shouldShow = false;
                    }
                    else {
                        // Level 2: Specific element flag from ProductCardOrderEditor
                        shouldShow = resolvedProductFormatting?.showAddToCartButton !== false;
                    }
                    return shouldShow;
                case 'title':
                    return resolvedProductFormatting?.showTitle !== false;
                case 'price':
                    return resolvedProductFormatting?.showPrice !== false;
                case 'description':
                    return resolvedProductFormatting?.showDescription !== false;
                case 'variants':
                    return resolvedProductFormatting?.showVariantSelection !== false;
                default:
                    return true;
            }
        });
        return filtered;
    }, [resolvedProductFormatting, resolvedClientAIConfig?.addToCartEnabled]);
    // Product data processing
    const dataSource = useMemo(() => {
        if (productData) {
            return {
                title: productData.title || 'Product Title',
                price: productData.price ? productData.price / 100 : 0,
                description: productData.description || '',
                // Use the original product URL from AI, not the commerce.getProductData URL
                url: product?.url || productData.url || productData.handle || '#',
                images: productData.images || [],
                media: productData.media || [],
                variants: productData.variants || [],
                options: productData.options || [],
            };
        }
        return {
            title: product?.title || 'Product Title',
            price: product?.price || 0,
            description: product?.description || '',
            url: product?.url || product?.handle || '#',
            images: product?.images || [],
            media: product?.media || [],
            variants: product?.variants || [],
            options: product?.options || [],
        };
    }, [productData, product]);
    // Product images processing
    const productImages = useMemo(() => {
        const images = dataSource.images || [];
        const media = dataSource.media || [];
        const variants = dataSource.variants || [];
        if (media.length > 0) {
            return media.map((item, index) => {
                // Try to find which variant this media belongs to
                const associatedVariant = variants.find((variant) => variant.featured_media?.id === item.id);
                return {
                    id: `media-${item.id || index}`,
                    url: item.src || item.url || '',
                    alt: item.alt || dataSource.title,
                    mediaId: item.id,
                    variantId: associatedVariant?.id?.toString(),
                    variantCombination: associatedVariant ? {
                        // Map variant options to readable names
                        ...(dataSource.options?.reduce((acc, option, idx) => {
                            const value = associatedVariant.options?.[idx];
                            if (value) {
                                acc[option.name] = value;
                            }
                            return acc;
                        }, {}) || {}),
                    } : {},
                    isMain: index === 0,
                    available: true,
                };
            });
        }
        if (images.length > 0) {
            return images.map((item, index) => ({
                id: `image-${item.id || index}`,
                url: typeof item === 'string' ? item : (item.src || item.url || ''),
                alt: dataSource.title,
                variantCombination: {},
                isMain: index === 0,
                available: true,
            }));
        }
        return [];
    }, [dataSource]);
    // Variant groups processing
    const variantGroups = useMemo(() => {
        if (!dataSource.options || dataSource.options.length === 0) {
            return [];
        }
        return dataSource.options.map((option) => ({
            name: option.name,
            type: option.name.toLowerCase(),
            options: option.values.map((value, index) => ({
                id: `${option.name}-${index}`,
                value,
                available: true,
            })),
            required: true,
        }));
    }, [dataSource.options]);
    // Variants processing
    const variants = useMemo(() => {
        const sourceData = productData || product;
        return sourceData?.variants?.map((variant) => ({
            id: variant.id?.toString() || variant.name || variant.title,
            options: variant.options || [
                variant.option1, variant.option2, variant.option3,
            ].filter(Boolean),
            available: variant.available,
            price: variant.price ? variant.price / 100 : 0, // Convert from cents to dollars
            sku: variant.sku,
            featured_media: variant.featured_media, // Include featured media information
        })) || [];
    }, [productData, product]);
    useEffect(() => {
        const preloaded = product?.productPayload;
        if (preloaded && typeof preloaded === 'object') {
            setProductData(preloaded);
            setIsLoadingProduct(false);
            return;
        }
        const getData = async () => {
            const jsonData = await getProductData(product.url);
            setProductData(jsonData);
            setIsLoadingProduct(false);
        };
        setIsLoadingProduct(true);
        getData();
    }, [product]);
    // Auto-select single variants using shared utility
    useEffect(() => {
        if (!productData?.variants)
            return;
        const productVariants = productData.variants || [];
        const autoSelectVariant = shouldAutoSelectVariant(productVariants);
        if (autoSelectVariant && !selectedVariantId) {
            setSelectedVariantId(autoSelectVariant.id?.toString() || null);
            // Sync variant options if we have product options
            if (productData.options && autoSelectVariant.options) {
                const syncedOptions = {};
                productData.options.forEach((option, index) => {
                    if (autoSelectVariant.options[index]) {
                        syncedOptions[option.name] = autoSelectVariant.options[index];
                    }
                });
                setSelectedVariantOptions(syncedOptions);
            }
        }
    }, [productData, selectedVariantId]);
    // Get admin configuration (already declared above)
    // Get AI Search configuration - NEW for sizing and layout
    const aiSearchConfig = useMemo(() => {
        const rawAiSearchConfig = resolvedClientAIConfig?.aiSearchConfig;
        return resolveAiSearchConfig(rawAiSearchConfig);
    }, [resolvedClientAIConfig?.aiSearchConfig]);
    const { objectFit } = aiSearchConfig?.mainImage || { objectFit: 'contain' };
    const { customizable } = product || {};
    const personalizationConfig = aiSearchConfig?.personalization;
    // Initialize base image
    useEffect(() => {
        if (productImages.length > 0 && !baseImageId) {
            setBaseImageId(productImages[0].id);
        }
    }, [productImages, baseImageId]);
    // Sync image with variant selection
    useEffect(() => {
        if (Object.keys(selectedVariantOptions).length === 0 || productImages.length === 0) {
            return;
        }
        // Don't auto-sync if user manually selected an image
        if (userSelectedImageRef.current) {
            return;
        }
        // Check if all variants have the same image (no value in auto-sync)
        const allVariantImages = variants.map((v) => {
            const variantImage = productImages.find((img) => img.variantId === v.id
                || (img.mediaId && v.featured_media?.id === img.mediaId));
            return variantImage?.id || 'no-image';
        });
        const uniqueImages = [...new Set(allVariantImages)];
        if (uniqueImages.length <= 1) {
            return;
        }
        // Find the currently selected variant
        const selectedVariant = getSelectedVariant(selectedVariantId, selectedVariantOptions, variants, dataSource.options || []);
        if (selectedVariant) {
            // Try to find the image for this variant
            const variantImage = productImages.find((img) => img.variantId === selectedVariant.id
                || (img.mediaId && selectedVariant.featured_media?.id === img.mediaId));
            if (variantImage && variantImage.id !== baseImageId) {
                setBaseImageId(variantImage.id);
                setHoveredImageId(null);
            }
        }
    }, [
        selectedVariantOptions,
        variants,
        productImages,
        baseImageId,
        dataSource.options,
        selectedVariantId,
    ]);
    const handleVariantSelectionChange = React.useCallback((selection, variantId) => {
        // Reset add to cart state if we were previously in added state
        if (isAddedToCart) {
            setIsAddedToCart(false);
        }
        // Reset the user-selected-image flag to allow auto-sync again
        userSelectedImageRef.current = false;
        setSelectedVariantOptions(selection);
        // Sync selectedVariantId with the new selection
        if (variantId) {
            setSelectedVariantId(variantId);
        }
        else {
            // Use syncVariantStates to find the correct variant ID
            const syncedStates = syncVariantStates(null, selection, variants, dataSource.options || []);
            setSelectedVariantId(syncedStates.syncedVariantId);
        }
        // Check if all variants have the same image (no value in auto-sync)
        const allVariantImages = variants.map((v) => {
            const variantImage = productImages.find((img) => img.variantId === v.id
                || (img.mediaId && v.featured_media?.id === img.mediaId));
            return variantImage?.id || 'no-image';
        });
        const uniqueImages = [...new Set(allVariantImages)];
        if (uniqueImages.length <= 1) {
            return;
        }
        // Use the new helper function to find the appropriate image
        const newImageId = syncImageWithVariantSelection(selection, productImages, variants);
        if (newImageId && newImageId !== baseImageId) {
            setBaseImageId(newImageId);
            setHoveredImageId(null);
        }
        else if (!newImageId && productImages.length > 0) {
            // Fallback to first image if no variant image found
            setBaseImageId(productImages[0].id);
            setHoveredImageId(null);
        }
    }, [
        isAddedToCart,
        variants,
        dataSource.options,
        productImages,
        baseImageId,
        syncImageWithVariantSelection,
    ]);
    // Handle variant option hover for image preview
    const handleVariantOptionHover = React.useCallback((payload) => {
        variantHoverActiveRef.current = true;
        const previewImageId = syncImageWithVariantSelection(payload.previewSelection, productImages, variants);
        if (previewImageId) {
            setHoveredImageId(previewImageId);
        }
    }, [productImages, variants, syncImageWithVariantSelection]);
    const handleVariantOptionLeave = React.useCallback(() => {
        variantHoverActiveRef.current = false;
        setHoveredImageId(null);
    }, []);
    // Handle add to cart functionality
    const handleAddToCart = async () => {
        const selectedVariant = getSelectedVariant(selectedVariantId, selectedVariantOptions, variants, dataSource.options || []);
        const currentSelectedVariantId = selectedVariant?.id?.toString() || null;
        const isValidSelection = validateVariantSelection(currentSelectedVariantId, variants);
        if (!isValidSelection || !selectedVariant || selectedVariant.available === false) {
            return;
        }
        // Clear any previous error
        setAddToCartError(null);
        setIsAddingToCart(true);
        try {
            const addToCartPayload = {
                source: 'microcommerce-ai-embed',
                type: 'addToCart',
                instanceId,
                details: {
                    variantId: selectedVariant.id,
                    quantity: Math.max(1, Number.isFinite(quantity) ? Number(quantity) : 1),
                },
            };
            window.parent.postMessage(addToCartPayload, '*');
            // Set timeout for response (3 seconds)
            const timeoutId = setTimeout(() => {
                console.error('[ProductDetailPanel] Add to cart timeout - no response received');
                setAddToCartError('Request timed out. Please try again.');
                setIsAddingToCart(false);
            }, 3000);
            setAddToCartTimeoutId(timeoutId);
        }
        catch (error) {
            console.error('[ProductDetailPanel] Add to cart error:', error);
            setAddToCartError('Failed to send request. Please try again.');
            setIsAddingToCart(false);
        }
    };
    // Listen for addToCartResult response
    useEffect(() => {
        const handleAddToCartResponse = (event) => {
            if (event.data.source === 'microcommerce-ai-embed' && event.data.type === 'addToCartResult') {
                // Only process response if it matches this ProductDetailPanel's instanceId
                if (event.data.instanceId !== instanceId) {
                    return;
                }
                setIsAddingToCart(false);
                // Clear timeout since we got a response
                if (addToCartTimeoutId) {
                    clearTimeout(addToCartTimeoutId);
                    setAddToCartTimeoutId(null);
                }
                if (event.data.data.success) {
                    setAddToCartError(null);
                    setIsAddedToCart(true);
                }
                else {
                    setAddToCartError(event.data.data.error || 'Failed to add item to cart');
                }
            }
        };
        window.addEventListener('message', handleAddToCartResponse);
        return () => window.removeEventListener('message', handleAddToCartResponse);
    }, [dataSource.title, instanceId, addToCartTimeoutId]);
    const isCheckoutFlowVisible = isAddedToCart && resolvedProductFormatting?.showCheckoutFlow !== false;
    useEffect(() => {
        if (isCheckoutFlowVisible && checkoutFlowRef.current) {
            checkoutFlowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isCheckoutFlowVisible]);
    const elements = {
        title: (resolvedProductFormatting?.showTitle !== false && dataSource.title ? (_jsx(Box, { sx: { mb: 1 }, children: _jsx(Typography, { variant: aiSearchConfig.typography.panelTitleVariant || 'h5', sx: {
                    fontWeight: 700,
                    fontSize: aiSearchConfig.typography.titleFontSize,
                    lineHeight: 1.25,
                    color: 'inherit',
                    ...(aiSearchConfig.typography.panelTitleMaxLines && {
                        display: '-webkit-box',
                        WebkitLineClamp: aiSearchConfig.typography.panelTitleMaxLines,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }),
                }, children: dataSource.title }) }, "title")) : null),
        price: (() => {
            if (resolvedProductFormatting?.showPrice === false)
                return null;
            // Calculate price based on selected variant or range
            let displayPrice = null;
            let displayPriceRange = null;
            const displayOriginalPrice = null;
            const formatPrice = (price) => (formatPriceWithLocale(price, locale, currency));
            // Find the currently selected variant
            const selectedVariant = getSelectedVariant(selectedVariantId, selectedVariantOptions, variants, dataSource.options || []);
            if (selectedVariant) {
                // Show specific variant price
                displayPrice = selectedVariant.price;
                // TODO: Add compare_at_price support when available in variant data
            }
            else if (variants.length > 0) {
                // Show price range for all variants
                const allPrices = variants.map((v) => v.price).filter((p) => p > 0);
                if (allPrices.length > 0) {
                    const minPrice = Math.min(...allPrices);
                    const maxPrice = Math.max(...allPrices);
                    if (minPrice === maxPrice) {
                        displayPrice = minPrice;
                    }
                    else {
                        displayPriceRange = `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
                    }
                }
            }
            else if (dataSource.price) {
                // Fallback to dataSource price
                displayPrice = dataSource.price;
            }
            if (!displayPrice && !displayPriceRange)
                return null;
            return (_jsxs(Box, { sx: { mb: 1 }, children: [displayOriginalPrice && displayOriginalPrice !== displayPrice && (_jsx(Typography, { variant: "h6", sx: {
                            textDecoration: 'line-through',
                            color: 'inherit',
                            display: 'inline-block',
                            mr: 1,
                            fontSize: '0.9rem',
                        }, children: formatPrice(displayOriginalPrice) })), displayPriceRange && (_jsx(Typography, { variant: "h6", sx: {
                            fontWeight: 'bold',
                            fontSize: aiSearchConfig.typography.priceFontSize,
                            color: 'inherit',
                        }, children: displayPriceRange })), displayPrice && (_jsx(Typography, { variant: "h6", sx: {
                            fontWeight: 'bold',
                            fontSize: aiSearchConfig.typography.priceFontSize,
                            color: 'inherit',
                            display: 'inline-block',
                        }, children: formatPrice(displayPrice) }))] }, "price"));
        })(),
        description: (() => {
            if (resolvedProductFormatting?.showDescription === false
                || !dataSource.description)
                return null;
            return (_jsx(Box, { sx: { mb: 1 }, children: _jsx(DescriptionRenderer, { description: dataSource.description, maxLength: aiSearchConfig.description.panelMaxLength, showSeeMore: aiSearchConfig.description.showSeeMore, stylesheet: storeStylesheet, isLoading: isLoadingProduct }) }, "description"));
        })(),
        variants: (() => {
            if (resolvedProductFormatting?.showVariantSelection === false
                || variantGroups.length === 0
                || variants.length <= 1)
                return null;
            return (_jsx(Box, { sx: { mb: 1 }, children: _jsx(VariantSelectorAdvanced, { variantGroups: variantGroups, variants: variants, selectedVariantOptions: selectedVariantOptions, onVariantSelectionChange: handleVariantSelectionChange, onOptionHover: isMobileView ? undefined : handleVariantOptionHover, onOptionLeave: isMobileView ? undefined : handleVariantOptionLeave, disableTooltip: isMobileView, dataSource: dataSource, aiSearchConfig: aiSearchConfig, enableImageSwatching: aiSearchConfig?.variantPhotoSwatches?.enabled ?? true, stylesheet: storeStylesheet, isLoading: isLoadingProduct }) }, "variants"));
        })(),
        addToCartButton: (() => {
            // Build button texts from admin configuration
            const buttonTexts = {
                addToBag: resolvedProductFormatting?.buttonText || tokens.getValue('addToBagLabel'),
                addedToCart: resolvedProductFormatting?.addedToCartText || tokens.getValue('addedToCartLabel'),
                loading: resolvedProductFormatting?.loadingText || tokens.getValue('addingLabel'),
                soldOut: resolvedProductFormatting?.soldOutText || tokens.getValue('soldOutLabel'),
                selectAllOptions: resolvedProductFormatting?.selectAllOptionsText || tokens.getValue('selectAllOptionsLabel'),
            };
            const buttonState = getButtonState(selectedVariantOptions, dataSource.options || [], variants, isAddingToCart, isAddedToCart, buttonTexts);
            return (_jsxs(Box, { sx: { mb: 2 }, children: [resolvedProductFormatting?.showQuantitySelector !== false && (_jsx(Box, { sx: { mb: 2 }, children: _jsx(QuantityField, { quantity: quantity, setQuantity: setQuantity, disabled: isAddingToCart, minQuantity: 1, maxQuantity: 10 }) })), customizable && personalizationConfig?.enabled ? (_jsxs(Box, { sx: {
                            display: 'flex',
                            gap: 2,
                            flexDirection: 'row',
                            alignItems: 'center',
                        }, children: [_jsx(Button, { variant: "outlined", onClick: () => {
                                    const productUrl = dataSource.url;
                                    if (productUrl) {
                                        window.open(productUrl, '_top');
                                    }
                                    else {
                                        console.warn('[ProductDetailPanel] No product URL available');
                                    }
                                }, sx: {
                                    flex: 1,
                                    backgroundColor: 'transparent',
                                    color: replaceStylesheetGlobals(storeStylesheet, 'color_font') || '#1f2937',
                                    border: `1px solid ${replaceStylesheetGlobals(storeStylesheet, 'color_primary_disabled') || '#d1d5db'}`,
                                    borderRadius: resolvedProductFormatting?.buttonStyle?.borderRadius || 4,
                                    '&:hover': {
                                        backgroundColor: replaceStylesheetGlobals(storeStylesheet, 'color_background') || '#f9fafb',
                                        borderColor: replaceStylesheetGlobals(storeStylesheet, 'color_primary') || '#2563eb',
                                    },
                                }, children: personalizationConfig?.personalizeButtonText || 'Click to Personalize' }), _jsx(Button, { variant: "contained", onClick: handleAddToCart, disabled: buttonState.disabled || isAddingToCart, sx: {
                                    flex: 1,
                                    backgroundColor: resolvedProductFormatting?.buttonStyle?.backgroundColor
                                        ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.backgroundColor)
                                        : undefined,
                                    color: resolvedProductFormatting?.buttonStyle?.color
                                        ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.color)
                                        : undefined,
                                    '&:hover': {
                                        backgroundColor: resolvedProductFormatting?.buttonStyle?.hoverBackgroundColor
                                            ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.hoverBackgroundColor)
                                            : undefined,
                                    },
                                    '&:disabled': {
                                        backgroundColor: resolvedProductFormatting?.buttonStyle?.disabledBackgroundColor
                                            ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.disabledBackgroundColor)
                                            : undefined,
                                        color: resolvedProductFormatting?.buttonStyle?.disabledColor
                                            ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.disabledColor)
                                            : undefined,
                                    },
                                }, children: isAddingToCart ? (_jsxs(_Fragment, { children: [_jsx(CircularProgress, { size: 20, sx: { marginRight: 1 } }), buttonState.text] })) : (personalizationConfig?.addToCartWithoutPersonalizationText || 'Add to Cart without Personalization') })] })) : (
                    // Regular product - show See Details + Add to Cart
                    _jsxs(Box, { sx: {
                            display: 'flex',
                            gap: 2,
                            flexDirection: 'row',
                            alignItems: 'center',
                        }, children: [_jsx("button", { type: "button", onClick: () => {
                                    const productUrl = dataSource.url;
                                    if (productUrl) {
                                        window.open(productUrl, '_top');
                                    }
                                    else {
                                        console.warn('[ProductDetailPanel] No product URL available');
                                    }
                                }, style: {
                                    flex: 1,
                                    backgroundColor: 'transparent',
                                    color: replaceStylesheetGlobals(storeStylesheet, 'color_font') || '#1f2937',
                                    border: `1px solid ${replaceStylesheetGlobals(storeStylesheet, 'color_primary_disabled') || '#d1d5db'}`,
                                    padding: aiSearchConfig.detailsButton.padding,
                                    borderRadius: `${aiSearchConfig.detailsButton.borderRadius || resolvedProductFormatting?.buttonStyle?.borderRadius}px`,
                                    fontSize: aiSearchConfig.detailsButton.fontSize,
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                }, onMouseOver: (e) => {
                                    e.currentTarget.style.backgroundColor = replaceStylesheetGlobals(storeStylesheet, 'color_background') || '#f9fafb';
                                }, onMouseOut: (e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }, onFocus: (e) => {
                                    e.currentTarget.style.backgroundColor = replaceStylesheetGlobals(storeStylesheet, 'color_background') || '#f9fafb';
                                }, onBlur: (e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }, children: resolvedProductFormatting?.seeMoreDetailsText || tokens.getValue('seeMoreText') }), _jsx(Button, { variant: "contained", onClick: handleAddToCart, disabled: buttonState.disabled || isAddingToCart, sx: {
                                    flex: 1,
                                    backgroundColor: resolvedProductFormatting?.buttonStyle?.backgroundColor
                                        ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.backgroundColor)
                                        : undefined,
                                    color: resolvedProductFormatting?.buttonStyle?.color
                                        ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.color)
                                        : undefined,
                                    '&:hover': {
                                        backgroundColor: resolvedProductFormatting?.buttonStyle?.hoverBackgroundColor
                                            ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.hoverBackgroundColor)
                                            : undefined,
                                    },
                                    '&:disabled': {
                                        backgroundColor: resolvedProductFormatting?.buttonStyle?.disabledBackgroundColor
                                            ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.disabledBackgroundColor)
                                            : undefined,
                                        color: resolvedProductFormatting?.buttonStyle?.disabledColor
                                            ? replaceStylesheetGlobals(storeStylesheet, resolvedProductFormatting.buttonStyle.disabledColor)
                                            : undefined,
                                    },
                                }, children: isAddingToCart ? (_jsxs(_Fragment, { children: [_jsx(CircularProgress, { size: 20, sx: { marginRight: 1 } }), buttonState.text] })) : (buttonState.text) })] })), addToCartError && (_jsx(Typography, { variant: "body2", sx: {
                            color: 'error.main',
                            fontSize: aiSearchConfig.typography.errorFontSize,
                            textAlign: 'center',
                            mb: 1,
                        }, children: addToCartError })), _jsx("div", { ref: checkoutFlowRef, children: _jsx(CheckoutFlow, { isVisible: isCheckoutFlowVisible, productTitle: dataSource.title, checkoutText: resolvedProductFormatting?.checkoutText || tokens.getValue('checkoutLabel'), onCheckout: () => {
                                if (window.parent) {
                                    window.parent.postMessage({
                                        source: 'microcommerce-ai-embed',
                                        type: 'checkout',
                                        checkoutUrl: resolvedProductFormatting?.checkoutUrl || '/checkout',
                                    }, '*');
                                }
                            }, containerSX: { mt: 2, p: 2, borderRadius: 2 }, buttonSX: { fontSize: '1rem', py: 1.5 }, stylesheet: storeStylesheet, buttonStyle: resolvedProductFormatting?.buttonStyle }) })] }, "addToCartButton"));
        })(),
        reviews: null, // Commented out like EnhancedProductCard
        productTabs: null, // Future feature
    };
    if (isLoadingProduct) {
        return (_jsxs(Box, { sx: {
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }, ref: sidebarRef, children: [_jsx(Box, { sx: {
                        width: '100%',
                        height: '30vh',
                        minHeight: '200px',
                        mb: 2,
                        borderRadius: 1,
                        overflow: 'hidden',
                    }, children: _jsx(Skeleton, { variant: "rectangular", width: "100%", height: "100%" }) }), _jsx(Box, { sx: {
                        display: 'flex',
                        gap: 1,
                        mb: 3,
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                    }, children: [1, 2, 3, 4].map((i) => (_jsx(Skeleton, { variant: "rectangular", width: 76, height: 76, sx: { flexShrink: 0, borderRadius: 1 } }, i))) }), _jsx(Skeleton, { variant: "text", width: "80%", height: 32, sx: { mb: 1 } }), _jsx(Skeleton, { variant: "text", width: "60%", height: 24, sx: { mb: 2 } }), _jsx(Skeleton, { variant: "text", width: "40%", height: 28, sx: { mb: 3 } }), _jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Skeleton, { variant: "text", width: "30%", height: 20, sx: { mb: 1 } }), _jsx(Box, { sx: { display: 'flex', gap: 1, flexWrap: 'wrap' }, children: [1, 2, 3].map((i) => (_jsx(Skeleton, { variant: "rectangular", width: 60, height: 32, sx: { borderRadius: 2 } }, i))) })] }), _jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Skeleton, { variant: "text", width: "25%", height: 20, sx: { mb: 1 } }), _jsx(Skeleton, { variant: "text", width: "100%", height: 16, sx: { mb: 0.5 } }), _jsx(Skeleton, { variant: "text", width: "95%", height: 16, sx: { mb: 0.5 } }), _jsx(Skeleton, { variant: "text", width: "85%", height: 16, sx: { mb: 0.5 } }), _jsx(Skeleton, { variant: "text", width: "90%", height: 16 })] }), _jsxs(Box, { sx: { display: 'flex', gap: 2, mb: 2 }, children: [_jsx(Skeleton, { variant: "rectangular", width: 80, height: 40, sx: { borderRadius: 1 } }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40, sx: { borderRadius: 1 } })] })] }));
    }
    return (_jsx("div", { ref: sidebarRef, className: "product-sidebar", style: {
            height: '100%',
            backgroundColor: '#ffffff',
        }, children: _jsxs("div", { style: {
                maxHeight: '750px', // Limitación para testear - actúa como "vagón en el carril"
                overflow: 'auto',
                height: '100%',
            }, children: [_jsx("div", { className: "main-image-section", style: {
                        height: aiSearchConfig.mainImage.height,
                        maxHeight: '400px',
                        minHeight: aiSearchConfig.mainImage.minHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0',
                        backgroundColor: '#ffffff',
                        borderBottom: '1px solid #e5e7eb',
                    }, children: displayImageId && productImages.length > 0 ? (() => {
                        const currentImage = productImages.find((img) => img.id === displayImageId);
                        return (_jsx("img", { className: "main-image", src: currentImage?.url || productImages[0].url, alt: dataSource?.title || 'Product Image', style: {
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: objectFit || 'contain',
                                borderRadius: aiSearchConfig.mainImage.borderRadius || 0,
                                aspectRatio: aiSearchConfig.mainImage.aspectRatio,
                            } }));
                    })() : (_jsx("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '8px',
                            color: '#6b7280',
                            fontSize: '0.875rem',
                        }, children: "No image available" })) }), _jsxs("div", { className: "content-section", style: {
                        padding: `${aiSearchConfig.container.padding}px`,
                        backgroundColor: 'white',
                        // Height is auto - content flows naturally
                    }, children: [productImages.length > 1 && (_jsx("div", { className: "thumbnail-carousel", style: {
                                marginBottom: `${aiSearchConfig.container.gap}px`,
                            }, children: _jsx("div", { className: "thumbnails-container", style: {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: `${aiSearchConfig.thumbnails.gap}px`,
                                    overflowX: 'auto',
                                    scrollBehavior: 'smooth',
                                    paddingBottom: '4px',
                                }, children: productImages.map((image) => (_jsx("div", { className: `thumbnail ${baseImageId === image.id ? 'selected' : ''}`, role: "button", tabIndex: 0, onClick: () => {
                                        // Mark that user manually selected an image
                                        userSelectedImageRef.current = true;
                                        setBaseImageId(image.id);
                                        setHoveredImageId(null);
                                        // Check if this thumbnail is associated with a variant
                                        const variantInfo = getVariantForThumbnail(image.id, productImages);
                                        if (variantInfo.variantCombination
                                            && Object.keys(variantInfo.variantCombination).length > 0) {
                                            setSelectedVariantOptions(variantInfo.variantCombination);
                                            // Sync selectedVariantId with the new selection
                                            const syncedStates = syncVariantStates(null, // Clear current ID to force recalculation
                                            variantInfo.variantCombination, variants, dataSource.options || []);
                                            setSelectedVariantId(syncedStates.syncedVariantId);
                                        }
                                    }, onKeyDown: (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            // Mark that user manually selected an image
                                            userSelectedImageRef.current = true;
                                            setBaseImageId(image.id);
                                            setHoveredImageId(null);
                                            // Check if this thumbnail is associated with a variant
                                            const variantInfo = getVariantForThumbnail(image.id, productImages);
                                            if (variantInfo.variantCombination
                                                && Object.keys(variantInfo.variantCombination).length > 0) {
                                                setSelectedVariantOptions(variantInfo.variantCombination);
                                                // Sync selectedVariantId with the new selection
                                                const syncedStates = syncVariantStates(null, // Clear current ID to force recalculation
                                                variantInfo.variantCombination, variants, dataSource.options || []);
                                                setSelectedVariantId(syncedStates.syncedVariantId);
                                            }
                                        }
                                    }, style: {
                                        width: `${aiSearchConfig.thumbnails.width}px`,
                                        height: `${aiSearchConfig.thumbnails.height}px`,
                                        borderRadius: `${aiSearchConfig.thumbnails.borderRadius}px`,
                                        border: baseImageId === image.id
                                            ? `${aiSearchConfig.thumbnails.selectedBorderWidth}px solid ${replaceStylesheetGlobals(storeStylesheet, 'color_primary') || '#2563eb'}`
                                            : `${aiSearchConfig.thumbnails.selectedBorderWidth}px solid transparent`,
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        transition: 'border-color 0.2s',
                                    }, onMouseOver: (e) => {
                                        if (baseImageId !== image.id && !variantHoverActiveRef.current) {
                                            e.currentTarget.style.border = `${aiSearchConfig.thumbnails.hoverBorderWidth}px solid ${replaceStylesheetGlobals(storeStylesheet, 'color_primary_disabled') || '#d1d5db'}`;
                                            setHoveredImageId(image.id);
                                        }
                                    }, onMouseOut: (e) => {
                                        if (baseImageId !== image.id) {
                                            e.currentTarget.style.border = `${aiSearchConfig.thumbnails.hoverBorderWidth}px solid transparent`;
                                            setHoveredImageId(null);
                                        }
                                    }, onFocus: (e) => {
                                        if (baseImageId !== image.id && !variantHoverActiveRef.current) {
                                            e.currentTarget.style.border = `${aiSearchConfig.thumbnails.hoverBorderWidth}px solid ${replaceStylesheetGlobals(storeStylesheet, 'color_primary_disabled') || '#d1d5db'}`;
                                            setHoveredImageId(image.id);
                                        }
                                    }, onBlur: (e) => {
                                        if (baseImageId !== image.id) {
                                            e.currentTarget.style.border = `${aiSearchConfig.thumbnails.hoverBorderWidth}px solid transparent`;
                                            setHoveredImageId(null);
                                        }
                                    }, children: _jsx("img", { src: image.url, alt: image.alt, style: {
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        } }) }, image.id))) }) })), allowedElements.map((elementType) => (elements[elementType]))] })] }) }));
};
ProductDetailPanel.defaultProps = {};
export default ProductDetailPanel;
//# sourceMappingURL=ProductDetailPanel.js.map